// SUB-OFFICE 7 — THE authored floor plan, as plain data. One contiguous floor,
// ~60m x 34m, 1 unit = 1m. X runs west(-30)..east(+30); Z runs north(-17,
// far from camera)..south(+17, near camera). The camera looks roughly north,
// so south-facing wall faces carry the readable surfaces.
//
// Geometry is built from this data in render/world.ts; collision in
// world/collision.ts; interactables consumed by input + entities.
// Mezzanine and Dept-6 interiors are NOT authored — their locked doors are.

import { PAL } from '../palette';

export interface WallSeg {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  h: number;
}

export interface RoomDef {
  id: string;
  name: string;
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  tint: number; // floor canvas base tint
  res: number; // floor canvas resolution (px, square)
  pools: [number, number][]; // painted fluorescent pool centers (world xz)
  dim: number; // 0..1 extra darkening (Registry 0.4, Recess emissive-free)
}

/** rot: 0 = flat against a wall facing SOUTH (+z, toward camera);
 *  90 = on a west wall facing EAST; -90 = on an east wall facing WEST. */
export interface WallProp {
  x: number;
  z: number;
  y: number;
  rot: 0 | 90 | -90;
}

export interface LockedDoor extends WallProp {
  id: 'dept6' | 'mezzanine' | 'form99' | 'vault' | 'lift';
  w: number;
  h: number;
  stencil: string | null; // stenciled text, or null (unmarked)
  line: string; // chyron line on interact
}

export interface Interactable {
  id: string;
  kind:
    | 'dispenser'
    | 'bench'
    | 'drawerWall'
    | 'lectern'
    | 'window'
    | 'door'
    | 'turnstile'
    | 'gallery'
    | 'plaque'
    | 'podium'
    | 'board';
  x: number;
  z: number;
  y: number;
  r: number; // proxy sphere radius for the cursor raycast
  prompt: string; // context line while in range / hovered
}

export interface FloorDecal {
  kind: 'yellowLine' | 'noStand' | 'proceed' | 'queueLine';
  x: number;
  z: number;
  rot: number; // degrees, CCW about Y
  sx: number; // world size x (before rot)
  sz: number;
}

export interface ZoneRect {
  kind: 'yellow' | 'noStand';
  x0: number;
  z0: number;
  x1: number;
  z1: number;
}

export interface SolidRect {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
}

// ---------------------------------------------------------------------------
// Perimeter + partitions. South/near walls low so the camera sees over them.
// Locked doors are decoration ON solid walls — collision never opens for them.
// ---------------------------------------------------------------------------

export const WALL_H = 4.5;
export const WALL_H_LOW = 2.2;
export const WALL_H_PART = 3.0;
export const WALL_T = 0.25;

export const WALLS: WallSeg[] = [
  // perimeter — NOTE (M-C): the old north (z=-17) and west (x=-30) perimeter
  // walls are now the MEMBRANE between the Public Concourse and the Service
  // Ring; they keep every v1 fixture and gain door gaps into the new rooms.
  // north membrane (window row wall) — gaps: Inspection Row staff door at x=2,
  // Intake -> Mail Room door at x=24
  { x0: -30, z0: -17, x1: 0.9, z1: -17, h: WALL_H },
  { x0: 3.1, z0: -17, x1: 22.9, z1: -17, h: WALL_H },
  { x0: 25.1, z0: -17, x1: 30, z1: -17, h: WALL_H },
  { x0: 30, z0: -17, x1: 30, z1: 17, h: WALL_H }, // east
  // west membrane (vault wall) — gap: Archive -> Lost & Found Annex at z=-8
  // (clear of the archive west-wall poster at z=-11; Registry stays sealed as
  // an intentional dramatic dead end).
  { x0: -30, z0: -17, x1: -30, z1: -9, h: WALL_H },
  { x0: -30, z0: -7, x1: -30, z1: 17, h: WALL_H },
  { x0: -30, z0: 17, x1: 30, z1: 17, h: WALL_H_LOW }, // south (near camera, low)
  // intake | hall partition x=14 (gaps: turnstile z[-6,-3], gallery z[7,10])
  { x0: 14, z0: -17, x1: 14, z1: -6, h: WALL_H_PART },
  { x0: 14, z0: -3, x1: 14, z1: 7, h: WALL_H_PART },
  { x0: 14, z0: 10, x1: 14, z1: 17, h: WALL_H_PART },
  // hall | west-wing partition x=-12 (gaps: archive z[-13,-10], rotunda z[-2,2], corridor z[7,10])
  { x0: -12, z0: -17, x1: -12, z1: -13, h: WALL_H_PART },
  { x0: -12, z0: -10, x1: -12, z1: -2, h: WALL_H_PART },
  { x0: -12, z0: 2, x1: -12, z1: 7, h: WALL_H_PART },
  { x0: -12, z0: 10, x1: -12, z1: 17, h: WALL_H_PART },
  // gallery north wall z=6 (FULL height: carries the 64 public-record frames)
  { x0: -12, z0: 6, x1: -8, z1: 6, h: WALL_H },
  { x0: -5, z0: 6, x1: 6, z1: 6, h: WALL_H },
  { x0: 9, z0: 6, x1: 14, z1: 6, h: WALL_H },
  // south-west cluster north wall z=6 (gaps: registry x[-27,-25], appeals x[-22,-19])
  { x0: -30, z0: 6, x1: -27, z1: 6, h: WALL_H_PART },
  { x0: -25, z0: 6, x1: -22, z1: 6, h: WALL_H_PART },
  { x0: -19, z0: 6, x1: -12, z1: 6, h: WALL_H_PART },
  // archive | rotunda partition z=-6 (gap x[-22,-19])
  { x0: -30, z0: -6, x1: -22, z1: -6, h: WALL_H_PART },
  { x0: -19, z0: -6, x1: -12, z1: -6, h: WALL_H_PART },
  // registry | appeals separation x=-24 (solid; registry is a dead end)
  { x0: -24, z0: 6, x1: -24, z1: 17, h: WALL_H_PART },
  // appeals block | recess block x=-18 (gap z[7.5,10])
  { x0: -18, z0: 6, x1: -18, z1: 7.5, h: WALL_H_PART },
  { x0: -18, z0: 10, x1: -18, z1: 17, h: WALL_H_PART },
  // appeals corridor | appeals office z=10 (door gap x[-22.5,-20.5])
  { x0: -24, z0: 10, x1: -22.5, z1: 10, h: WALL_H_PART },
  { x0: -20.5, z0: 10, x1: -18, z1: 10, h: WALL_H_PART },
  // recess corridor | recess room z=10 (door gap x[-16,-14])
  { x0: -18, z0: 10, x1: -16, z1: 10, h: WALL_H_PART },
  { x0: -14, z0: 10, x1: -12, z1: 10, h: WALL_H_PART },

  // =========================================================================
  // M-C SERVICE RING — north back-of-house strip z[-25,-17] + west spine
  // x[-38,-30]. New outer perimeter, then internal dividers with door gaps
  // that thread the two loops. All partitions WALL_H_PART so the FP eye reads
  // over them into the next bay (sightlines); perimeter full WALL_H.
  // =========================================================================
  // new outer perimeter
  { x0: -38, z0: -25, x1: 30, z1: -25, h: WALL_H }, // new north
  { x0: -38, z0: -25, x1: -38, z1: 17, h: WALL_H }, // new west (spine back)
  { x0: 30, z0: -25, x1: 30, z1: -17, h: WALL_H }, // east extension (north)
  { x0: -38, z0: 17, x1: -30, z1: 17, h: WALL_H_LOW }, // south foot (west spine, near)
  // north-ring E-W dividers (N-S walls); door gaps at z=-21 (spine door z=-20)
  { x0: 6, z0: -25, x1: 6, z1: -22.1, h: WALL_H_PART }, // Mail | Inspection
  { x0: 6, z0: -19.9, x1: 6, z1: -17, h: WALL_H_PART },
  { x0: -14, z0: -25, x1: -14, z1: -22.1, h: WALL_H_PART }, // Inspection | Descent
  { x0: -14, z0: -19.9, x1: -14, z1: -17, h: WALL_H_PART },
  { x0: -30, z0: -25, x1: -30, z1: -21.1, h: WALL_H_PART }, // Descent | Spine-N
  { x0: -30, z0: -18.9, x1: -30, z1: -17, h: WALL_H_PART },
  // west-spine E-W dividers (stacked rooms x[-38,-30]); door gaps at x=-34
  { x0: -38, z0: -17, x1: -34.9, z1: -17, h: WALL_H_PART }, // Spine-N | Lost&Found
  { x0: -33.1, z0: -17, x1: -30, z1: -17, h: WALL_H_PART },
  { x0: -38, z0: -6, x1: -34.9, z1: -6, h: WALL_H_PART }, // Lost&Found | Engraving
  { x0: -33.1, z0: -6, x1: -30, z1: -6, h: WALL_H_PART },
  { x0: -38, z0: 6, x1: -34.9, z1: 6, h: WALL_H_PART }, // Engraving | Retention
  { x0: -33.1, z0: 6, x1: -30, z1: 6, h: WALL_H_PART },
  // Copier Room alcove inside Retention: north wall z=11, east wall x=-33 (door z=14)
  { x0: -38, z0: 11, x1: -33, z1: 11, h: WALL_H_PART },
  { x0: -33, z0: 11, x1: -33, z1: 12.9, h: WALL_H_PART },
  { x0: -33, z0: 14.9, x1: -33, z1: 17, h: WALL_H_PART },
];

// ---------------------------------------------------------------------------
// Rooms (each gets its own floor canvas; ~60px/m; small rooms 512)
// ---------------------------------------------------------------------------

export const ROOMS: RoomDef[] = [
  {
    id: 'intake', name: 'INTAKE HALL', x0: 14, z0: -17, x1: 30, z1: 17,
    tint: PAL.bone, res: 1024, dim: 0,
    // [24,-6] is the dispenser's own pool: the first thing a new file must find
    // is lit from directly overhead so it reads as the destination.
    pools: [[18, -10], [24, -10], [24, -6], [18, -2], [24, -2], [18, 6], [24, 6], [20, 12], [26, 12]],
  },
  {
    id: 'hall', name: 'WINDOW ROW / WRITING FLOOR', x0: -12, z0: -17, x1: 14, z1: 6,
    tint: PAL.bone, res: 1024, dim: 0,
    pools: [[-10, -13], [-5, -13], [0, -13], [5, -13], [10, -13], [-8, -4], [-2, -4], [4, -4], [10, -4], [-8, 2], [0, 2], [8, 2]],
  },
  {
    id: 'gallery', name: 'PUBLIC RECORD GALLERY', x0: -12, z0: 6, x1: 14, z1: 17,
    tint: PAL.bone, res: 1024, dim: 0.08,
    pools: [[-6, 11], [1, 11], [8, 11]],
  },
  {
    id: 'archive', name: 'THE ARCHIVE', x0: -30, z0: -17, x1: -12, z1: -6,
    tint: PAL.bone, res: 1024, dim: 0.05,
    pools: [[-26, -12], [-20, -12], [-15, -11]],
  },
  {
    id: 'rotunda', name: 'THE ROTUNDA', x0: -30, z0: -6, x1: -12, z1: 6,
    tint: PAL.bone, res: 1024, dim: 0,
    pools: [[-24, 0], [-18, 0], [-15, 3]],
  },
  {
    id: 'registry', name: 'REGISTRY OF THE PURGED', x0: -30, z0: 6, x1: -24, z1: 17,
    tint: PAL.bone, res: 512, dim: 0.4,
    pools: [[-27, 11]],
  },
  {
    id: 'appealsCorr', name: 'APPEALS CORRIDOR', x0: -24, z0: 6, x1: -18, z1: 10,
    tint: PAL.wood, res: 512, dim: 0.3,
    pools: [[-21, 8]],
  },
  {
    id: 'appealsOffice', name: 'OFFICE OF APPEALS', x0: -24, z0: 10, x1: -18, z1: 17,
    tint: PAL.wood, res: 512, dim: 0.35,
    pools: [[-21, 13]],
  },
  {
    id: 'recessCorr', name: 'CORRIDOR', x0: -18, z0: 6, x1: -12, z1: 10,
    tint: PAL.ghost, res: 512, dim: 0.15,
    pools: [[-15, 8]],
  },
  {
    id: 'recess', name: 'RECESS ROOM', x0: -18, z0: 10, x1: -12, z1: 17,
    tint: PAL.ghost, res: 512, dim: 0.2,
    pools: [], // zero emissives; the loudest silence in the building
  },
  // ---- M-C SERVICE RING rooms -------------------------------------------
  // North back-of-house strip (Mail / Inspection / Descent) + west spine.
  {
    id: 'mailRoom', name: 'THE MAIL ROOM', x0: 6, z0: -25, x1: 30, z1: -17,
    tint: PAL.bone, res: 1024, dim: 0.12, // cold utility; brass catches the trunk glow
    pools: [[12, -21], [19, -21], [26, -21], [16, -23]],
  },
  {
    id: 'inspectionRow', name: 'INSPECTION ROW', x0: -14, z0: -25, x1: 6, z1: -17,
    tint: PAL.bone, res: 1024, dim: 0.04, // cold interrogation white (kept bright)
    pools: [[-10, -21], [-6, -21], [-2, -21], [2, -21], [-6, -23]],
  },
  {
    id: 'descentStair', name: 'THE DESCENT', x0: -30, z0: -25, x1: -14, z1: -17,
    tint: PAL.bone, res: 512, dim: 0.32, // cold + a rising phosphor wash from below
    pools: [[-22, -21]],
  },
  {
    id: 'spineN', name: 'WEST SERVICE SPINE', x0: -38, z0: -25, x1: -30, z1: -17,
    tint: PAL.bone, res: 512, dim: 0.34,
    pools: [[-34, -21]],
  },
  {
    id: 'lostFound', name: 'LOST & FOUND ANNEX', x0: -38, z0: -17, x1: -30, z1: -6,
    tint: PAL.ghost, res: 512, dim: 0.3,
    pools: [[-34, -11]],
  },
  {
    id: 'engraving', name: 'THE ENGRAVING OFFICE', x0: -38, z0: -6, x1: -30, z1: 6,
    tint: PAL.bone, res: 512, dim: 0.28,
    pools: [[-34, 0]],
  },
  {
    id: 'retention', name: 'THE RETENTION ANNEX', x0: -38, z0: 6, x1: -30, z1: 17,
    tint: PAL.bone, res: 512, dim: 0.4, // dim; the deepest drawer canyon
    pools: [[-34, 9], [-35, 14]],
  },
];

// ---------------------------------------------------------------------------
// The Machine's fixtures
// ---------------------------------------------------------------------------

/** 5 clerk windows on the north wall. Window index 1-5, west to east. */
export const WINDOW_X = [-10, -5, 0, 5, 10];
export const WINDOW_Z = -17; // wall line; counters protrude south
export const FORM99V_WINDOW = 4; // Window 4 carries the FORM 99-V slot

/** Window-3 flickering tube: the canonical 13s hand-keyed loop.
 *  [time s, intensity 0..1] pairs; linear hold between keys. */
export const W3_TUBE_LOOP: [number, number][] = [
  [0.0, 1.0], [3.1, 1.0], [3.15, 0.35], [3.25, 1.0], [3.4, 0.15], [3.55, 0.95],
  [6.8, 0.95], [6.9, 0.4], [7.0, 1.0],
  [9.2, 1.0], [9.25, 0.1], [9.45, 0.75], [9.6, 0.2], [9.8, 1.0],
  [12.4, 1.0], [12.5, 0.55], [12.6, 1.0], [13.0, 1.0],
];

/** Eye-lamp positions (heads hang at y=4.0). */
export const LAMPS: [number, number][] = [
  [18, -8], [24, -12], [20, 0], [26, 4], [17, 12],
  [-8, -12], [-2, -12], [4, -12], [10, -12],
  [-8, 0], [0, 0], [8, 0],
  [-4, 11], [8, 11],
  [-26, -12], [-18, -12], [-22, -8],
  [-18, 0], [-24, 3],
  [-21, 8], [-15, 8], [-27, 12],
  // M-C service ring
  [12, -21], [19, -21], [26, -21], // Mail Room
  [-10, -21], [-6, -21], [2, -21], // Inspection Row
  [-22, -21], // Descent Stairhead
  [-34, -21], [-34, -11], [-34, 0], [-34, 9], // West Spine bays
];
export const LAMP_Y = 4.0;

/** Chyron cornice quads (all skinned with the ONE live chyron texture). */
export const CHYRONS: { x: number; z: number; y: number; len: number; rot: 0 | 90 | -90 }[] = [
  { x: 1, z: -16.55, y: 3.9, len: 24, rot: 0 }, // window row
  { x: 22, z: -16.55, y: 3.9, len: 14, rot: 0 }, // intake
  { x: -20.5, z: -5.75, y: 2.7, len: 13, rot: 0 }, // rotunda
];

/** Pneumatic tube splines (xyz point lists; CatmullRom through each). */
export const TUBES: [number, number, number][][] = [
  // trunk: hugs the north wall ABOVE the chyron cornice, west to the Vault mouth
  [[11, 5.5, -15.9], [-14, 5.5, -15.9], [-24, 5.8, -10], [-28.5, 6.2, -3], [-29.3, 6.8, 0]],
  // window risers joining the trunk
  ...WINDOW_X.map((wx): [number, number, number][] => [
    [wx, 3.9, -16.5], [wx, 4.6, -16.3], [wx, 5.5, -16.0], [wx, 5.5, -15.92],
  ]),
  // M-C: the trunk branches NORTH over the membrane into the Mail Room, where
  // it drops to the 8-R switchboard — the pneumatic nervous system given a room
  [[11, 5.5, -15.9], [16, 5.6, -16.2], [16, 5.6, -19], [16, 4.4, -21.3]],
];
export const TUBE_RING_SPACING = 4;

/** The Adjudication Vault face: west wall of the rotunda. Never opens. */
export const VAULT = { x: -30, z: 0, w: 9, h: 8, beaconY: 8.6 };

// ---------------------------------------------------------------------------
// Locked doors (decoration on solid walls; the interiors do not ship)
// ---------------------------------------------------------------------------

export const DOORS: LockedDoor[] = [
  {
    id: 'dept6', x: -11.45, z: -17, y: 0, rot: 0, w: 0.95, h: 2.1,
    stencil: null, // unmarked. The green light leak IS the label.
    line: 'THIS DOOR HAS NO DEPARTMENT.',
  },
  {
    id: 'mezzanine', x: 30, z: -11.5, y: 0, rot: -90, w: 1.1, h: 2.2,
    stencil: 'STAIRWELL PENDING AUTHORIZATION',
    line: 'STAIRWELL PENDING AUTHORIZATION. AUTHORIZATION IS NOT PENDING.',
  },
  {
    // THE WORKING LIFT (M-B): the door that actually opens, on the Intake east
    // wall at z=-6, deliberately adjacent to the forever-pending Mezzanine door
    // above (Q2: the machine that decides, beside the stair that is pending).
    // The gameplay director routes 'door:lift' into the vertical core.
    id: 'lift', x: 30, z: -6, y: 0, rot: -90, w: 1.2, h: 2.3,
    stencil: 'THE LIFT',
    line: 'THE CAB HAS ARRIVED. IT WAS ALREADY HERE. IT IS ALWAYS ALREADY HERE.',
  },
  {
    id: 'form99', x: -30, z: 11.5, y: 0, rot: 90, w: 1.1, h: 2.2,
    stencil: 'FORM-99 PROCESSING',
    line: 'SEALED BY ORDER OF DEPARTMENT 2.',
  },
];

// ---------------------------------------------------------------------------
// Furniture / props (positions; geometry built procedurally)
// ---------------------------------------------------------------------------

export const WRITING_DESK_COUNT = 12;
export const DESKS: { x: number; z: number }[] = [
  { x: -8, z: -6 }, { x: -3, z: -6 }, { x: 2, z: -6 }, { x: 7, z: -6 },
  { x: -8, z: -2 }, { x: -3, z: -2 }, { x: 2, z: -2 }, { x: 7, z: -2 },
  { x: -8, z: 2 }, { x: -3, z: 2 }, { x: 2, z: 2 }, { x: 7, z: 2 },
  // index 12: the intake FORM 1 desk (geometry + solid ride the same list).
  // Placement note: >=1.0m clear of the FORM 3 lectern footprint — the two
  // solids must never form a capsule-trapping crevice (radius 0.35 => 0.7m).
  { x: 15.8, z: -1.6 },
];
export const LECTERN = { x: 11.5, z: 3 }; // Writing Floor ritual lectern
export const FORM3_LECTERN = { x: 18, z: -2 }; // intake ropes consent lectern

export const BENCHES: { x: number; z: number; rot: number }[] = [
  { x: 20, z: 7, rot: 0 }, { x: 24, z: 7, rot: 0 }, { x: 22, z: 11, rot: 0 },
  { x: 1, z: 12, rot: 0 },
  { x: -15, z: 13.5, rot: 0 }, // the Recess bench
];

export const DISPENSER = { x: 24, z: -6 };
/** The submit chain's terminal fixture: brass pillar + plunger on the
 *  Writing Floor — three steps from the nearest desk, as the ritual demands. */
export const TUBE_MOUTH = { x: 10.5, z: -8.5 };
/** Intake Form 1 desk: past the FORM 3 lectern, before the turnstile. */
export const FORM1_DESK = { x: 15.8, z: -1.6 };
export const SPLITFLAP = { x: 21, z: -16.6, y: 3.0, w: 3.2, h: 0.9 };
export const TURNSTILE = { x: 14, z: -4.5 };
export const PLAQUE = { x: 29.85, z: 1, y: 1.6, rot: -90 as const };
export const COPIER = { x: -27.5, z: 15.5 }; // in the Registry's sealed corner
// M-C promotions (ratified ruling Q3): the dedicated ring room is the verb's
// canonical FULL-grade home; the front-of-house bolt-on is kept as the grade-1
// teaser/overflow. The difficulty ladder becomes SPATIAL.
export const JUNCTION_FULL = { x: 16, z: -22 }; // 8-R switchboard — Mail Room (on the trunk branch)
export const JUNCTION_TAP = { x: -24, z: -3 }; // 8-R grade-1 tap — Rotunda trunk wall (kept)
export const DISC_FULL = { x: -6, z: -22.5 }; // 5-D forensic row — Inspection Row
export const DISC_TEASER = { x: 12, z: -15.3 }; // 5-D grade-0/1 intro — Window Row (kept)
export const LETTERHEAD_BOARD = { x: -17, z: -5.75, w: 6, h: 5.6 }; // pokes above its wall
export const PODIUMS: { x: number; z: number }[] = [
  { x: -21, z: -1 }, { x: -19.5, z: 0.8 }, { x: -22.5, z: 0.8 },
];

/** Poster placements (12 texts from voice.json, in listed order). */
export const POSTER_SPOTS: WallProp[] = [
  { x: 17.5, z: -16.9, y: 2.2, rot: 0 },
  { x: 26, z: -16.9, y: 2.2, rot: 0 },
  { x: 29.9, z: -5, y: 2.2, rot: -90 },
  { x: 29.9, z: 8, y: 2.2, rot: -90 },
  { x: -7.5, z: -16.9, y: 2.4, rot: 0 },
  { x: -2.5, z: -16.9, y: 2.4, rot: 0 },
  { x: 2.5, z: -16.9, y: 2.4, rot: 0 },
  { x: 7.5, z: -16.9, y: 2.4, rot: 0 },
  { x: -29.9, z: -11, y: 2.2, rot: 90 },
  { x: -29.9, z: 9, y: 2.0, rot: 90 },
  { x: -23.9, z: 8, y: 1.9, rot: 90 },
  { x: -15, z: 10.1, y: 1.9, rot: 0 },
];

/** Archive drawer wall: 10 cols x 6 rows on the archive north wall. */
export const DRAWERS = {
  x0: -29, x1: -13, z: -16.85, rows: 6, cols: 10, y0: 0.35, rowH: 0.5,
};

/** Gallery frames: 2 rows on the gallery north wall's south face. */
export const GALLERY_WALL = {
  z: 6.15, // south face of the z=6 wall
  segments: [
    { x0: -11.6, x1: -8.4 },
    { x0: -4.6, x1: 5.6 },
    { x0: 9.4, x1: 13.6 },
  ],
  rows: [1.5, 2.7], // frame center heights
  frame: { w: 0.52, h: 0.68, gap: 0.62 },
  count: 64,
};

/** Registry: struck-through brass plaques of the purged. */
export const REGISTRY_PLAQUES = {
  z: 6.15, x0: -29.6, x1: -24.4, rows: [1.2, 1.8, 2.4], perRow: 4,
};

// ---------------------------------------------------------------------------
// Wayfinding paint (each entry becomes a crisp decal quad; never blurred)
// ---------------------------------------------------------------------------

export const DECALS: FloorDecal[] = [
  // PROCEED arrows (rot 0 => arrow points -z/north; rot -90 => west).
  // Onboarding law: the FIRST leg MUST reach the TICKET DISPENSER (24,-6) —
  // spawn (24,1) walks north under the arrows to the dispenser; only THEN do
  // the arrows bend west toward the ropes (Form 3) and the turnstile.
  { kind: 'proceed', x: 24, z: -1.0, rot: 0, sx: 1.4, sz: 2.2 },
  { kind: 'proceed', x: 24, z: -3.6, rot: 0, sx: 1.4, sz: 2.2 },
  // dispenser -> Form 3 ropes / turnstile (west)
  { kind: 'proceed', x: 20.5, z: -5.6, rot: -90, sx: 1.4, sz: 2.2 },
  { kind: 'proceed', x: 16.5, z: -4.8, rot: -90, sx: 1.4, sz: 2.2 },
  // window queue lines
  ...WINDOW_X.map((wx): FloorDecal => ({ kind: 'queueLine', x: wx, z: -13.2, rot: 0, sx: 1.8, sz: 0.35 })),
  // main circulation line across the hall
  { kind: 'yellowLine', x: 1, z: 4.5, rot: 0, sx: 25, sz: 0.18 },
  { kind: 'yellowLine', x: 22, z: -7.8, rot: 90, sx: 10, sz: 0.18 },
  // DO NOT STAND: before the Vault, and inside the turnstile throat
  { kind: 'noStand', x: -27.5, z: 0, rot: 0, sx: 3.2, sz: 3.2 },
  { kind: 'noStand', x: 15.6, z: -4.5, rot: 0, sx: 2.4, sz: 2.4 },
  // ---- M-C service-ring wayfinding (FP legibility: every threshold arrowed) --
  { kind: 'proceed', x: 24, z: -15.6, rot: 0, sx: 1.4, sz: 2.0 }, // Intake -> Mail Room
  { kind: 'proceed', x: 24, z: -19.0, rot: 0, sx: 1.4, sz: 2.0 },
  { kind: 'proceed', x: 2, z: -15.6, rot: 0, sx: 1.4, sz: 2.0 }, // Window Row -> Inspection Row
  { kind: 'proceed', x: -28, z: -8, rot: -90, sx: 1.4, sz: 2.0 }, // Archive -> Lost & Found (west)
  // circulation lines: the Mail Room cross-aisle and the West Spine's N-S trunk
  { kind: 'yellowLine', x: 18, z: -21, rot: 0, sx: 22, sz: 0.18 },
  { kind: 'yellowLine', x: -34, z: -5, rot: 90, sx: 22, sz: 0.18 },
];

/** Silent telemetry zones (crossings counted, never announced). */
export const ZONES: ZoneRect[] = [
  { kind: 'yellow', x0: -11.5, z0: 4.32, x1: 13.5, z1: 4.68 },
  { kind: 'yellow', x0: 21.82, z0: -12.8, x1: 22.18, z1: -2.8 },
  { kind: 'noStand', x0: -29.1, z0: -1.6, x1: -25.9, z1: 1.6 },
  { kind: 'noStand', x0: 14.4, z0: -5.7, x1: 16.8, z1: -3.3 },
];

/** Prop collision footprints (walls come from WALLS). */
export const SOLIDS: SolidRect[] = [
  ...DESKS.map((d) => ({ x0: d.x - 0.8, z0: d.z - 0.4, x1: d.x + 0.8, z1: d.z + 0.4 })),
  ...BENCHES.map((b) => ({ x0: b.x - 0.9, z0: b.z - 0.3, x1: b.x + 0.9, z1: b.z + 0.3 })),
  { x0: DISPENSER.x - 0.45, z0: DISPENSER.z - 0.45, x1: DISPENSER.x + 0.45, z1: DISPENSER.z + 0.45 },
  { x0: LECTERN.x - 0.4, z0: LECTERN.z - 0.4, x1: LECTERN.x + 0.4, z1: LECTERN.z + 0.4 },
  { x0: FORM3_LECTERN.x - 0.4, z0: FORM3_LECTERN.z - 0.4, x1: FORM3_LECTERN.x + 0.4, z1: FORM3_LECTERN.z + 0.4 },
  ...PODIUMS.map((p) => ({ x0: p.x - 0.5, z0: p.z - 0.5, x1: p.x + 0.5, z1: p.z + 0.5 })),
  { x0: COPIER.x - 0.8, z0: COPIER.z - 0.6, x1: COPIER.x + 0.8, z1: COPIER.z + 0.6 },
  { x0: TUBE_MOUTH.x - 0.3, z0: TUBE_MOUTH.z - 0.3, x1: TUBE_MOUTH.x + 0.3, z1: TUBE_MOUTH.z + 0.3 },
  // clerk window counters protrude from the north wall
  ...WINDOW_X.map((wx) => ({ x0: wx - 1.1, z0: -17, x1: wx + 1.1, z1: -16.1 })),
  // archive drawer wall protrudes
  { x0: DRAWERS.x0 - 0.2, z0: -17, x1: DRAWERS.x1 + 0.2, z1: -16.3 },
  // vault rings protrude into the rotunda
  { x0: -30, z0: -4.5, x1: -29.2, z1: 4.5 },
  // 2-V gauge manifold board on the vault wall, NW corner of the rotunda
  { x0: -30, z0: -5.6, x1: -29.1, z1: -3.8 },
  // ---- M-C service-ring station + fixture footprints ----------------------
  // 8-R full switchboard (Mail Room) + grade-1 tap (Rotunda)
  { x0: JUNCTION_FULL.x - 0.75, z0: JUNCTION_FULL.z - 0.5, x1: JUNCTION_FULL.x + 0.75, z1: JUNCTION_FULL.z + 0.4 },
  { x0: JUNCTION_TAP.x - 0.5, z0: JUNCTION_TAP.z - 0.4, x1: JUNCTION_TAP.x + 0.5, z1: JUNCTION_TAP.z + 0.35 },
  // 5-D forensic row (Inspection Row, 3 wickets) + grade-0/1 intro (Window Row)
  { x0: DISC_FULL.x - 4.0, z0: DISC_FULL.z - 0.45, x1: DISC_FULL.x + 4.0, z1: DISC_FULL.z + 0.45 },
  { x0: DISC_TEASER.x - 0.9, z0: -17, x1: DISC_TEASER.x + 0.9, z1: -15.0 },
  // 5-M cradle bank in the Mail Room (conveyance origins)
  { x0: 21.5, z0: -24.7, x1: 28.5, z1: -23.9 },
  // west-spine dread fixtures (deep drawers / benches against the x=-38 wall)
  { x0: -38, z0: 7, x1: -37.3, z1: 16 }, // Retention drawer canyon
  { x0: -37.9, z0: 12, x1: -36.9, z1: 16 }, // Copier alcove disposal block
  { x0: -34.9, z0: -0.6, x1: -33.1, z1: 0.6 }, // Engraving workbench (mid-room)
  { x0: -38, z0: -15, x1: -37.4, z1: -8 }, // Lost & Found shelving
];

// ---------------------------------------------------------------------------
// Interactables (proxy spheres; prompts print on the context line)
// ---------------------------------------------------------------------------

export const INTERACTABLES: Interactable[] = [
  { id: 'dispenser', kind: 'dispenser', x: DISPENSER.x, z: DISPENSER.z, y: 1.1, r: 1.2, prompt: 'TICKET DISPENSER — E TO TAKE A TICKET' },
  { id: 'turnstile', kind: 'turnstile', x: TURNSTILE.x, z: TURNSTILE.z, y: 1.0, r: 1.4, prompt: 'TURNSTILE — PROCEED' },
  { id: 'plaque', kind: 'plaque', x: PLAQUE.x, z: PLAQUE.z, y: PLAQUE.y, r: 1.4, prompt: 'BRASS PLAQUE — E TO READ' },
  { id: 'form3', kind: 'lectern', x: FORM3_LECTERN.x, z: FORM3_LECTERN.z, y: 1.1, r: 1.2, prompt: 'FORM 3 — CONSENT TO AMBIENCE — E TO REVIEW' },
  { id: 'lectern', kind: 'lectern', x: LECTERN.x, z: LECTERN.z, y: 1.1, r: 1.2, prompt: 'THE LECTERN — RITUALS ARE PERFORMED STANDING' },
  { id: 'splitflap', kind: 'board', x: SPLITFLAP.x, z: SPLITFLAP.z - 0.5, y: 3, r: 2.0, prompt: 'NOW SERVING — THE NUMBER IS NOT YOURS YET' },
  { id: 'drawers', kind: 'drawerWall', x: -21, z: -16.2, y: 1.5, r: 2.2, prompt: 'THE ARCHIVE — E TO LOCATE YOUR DRAWER' },
  { id: 'gallery', kind: 'gallery', x: 0.5, z: 6.6, y: 2, r: 2.6, prompt: 'PUBLIC RECORD — E TO EXAMINE AN ENTRY' },
  { id: 'podiums', kind: 'podium', x: -21, z: 0, y: 1.2, r: 1.8, prompt: 'LEAST DISAPPOINTING — TODAY’S THREE, UNDER GLASS' },
  { id: 'board', kind: 'board', x: LETTERHEAD_BOARD.x, z: -5.4, y: 2.4, r: 2.4, prompt: 'THE DAILY DIRECTIVE — ISSUED TO EVERYONE, INCLUDING YOU' },
  ...DOORS.map((d): Interactable => ({
    id: 'door:' + d.id, kind: 'door', x: d.x, z: d.z, y: 1.1, r: 1.3,
    prompt: d.id === 'dept6' ? 'A DOOR — E TO TRY IT ANYWAY' : (d.stencil || 'A DOOR') + ' — E TO TRY IT',
  })),
  ...WINDOW_X.map((wx, i): Interactable => ({
    id: 'window:' + (i + 1), kind: 'window', x: wx, z: -16, y: 1.4, r: 1.3,
    prompt: i + 1 === FORM99V_WINDOW
      ? 'WINDOW 4 — FORM 99-V IS AVAILABLE HERE'
      : 'WINDOW ' + (i + 1) + ' — NO SUMMONS IS ACTIVE FOR YOU',
  })),
  ...BENCHES.map((b, i): Interactable => ({
    id: 'bench:' + i, kind: 'bench', x: b.x, z: b.z, y: 0.6, r: 1.2,
    prompt: i === 4 ? 'THE RECESS BENCH — REST IS A RESOURCE' : 'BENCH — E TO SIT',
  })),
  ...DESKS.slice(0, WRITING_DESK_COUNT).map((d, i): Interactable => ({
    id: 'desk:' + i, kind: 'bench', x: d.x, z: d.z, y: 0.8, r: 1.15,
    prompt: 'TYPEWRITER DESK — COMPOSITION REQUIRES A FORM',
  })),
  { id: 'tubemouth', kind: 'lectern', x: TUBE_MOUTH.x, z: TUBE_MOUTH.z, y: 1.2, r: 1.25,
    prompt: 'PNEUMATIC TUBE MOUTH — SUBMISSIONS TRAVEL BY CANISTER' },
  { id: 'form1desk', kind: 'bench', x: FORM1_DESK.x, z: FORM1_DESK.z, y: 0.8, r: 1.2,
    prompt: 'THE FORM 1 DESK — REGISTRATION PRECEDES EVERYTHING' },
  { id: 'copier', kind: 'lectern', x: COPIER.x, z: COPIER.z, y: 1.0, r: 1.5,
    prompt: 'THE COPIER — CURRENTLY COLD' },
  { id: 'manifold', kind: 'lectern', x: -28.6, z: -4.7, y: 1.5, r: 1.6,
    prompt: 'THE GAUGE MANIFOLD — PRESSURE IS KEPT BY HANDS' },
  // ---- W2 physical-bench stations (prompts go live only while assigned) ----
  // 8-R full switchboard now lives in the Mail Room; grade-1 tap kept at Rotunda
  { id: 'junction', kind: 'lectern', x: JUNCTION_FULL.x, z: JUNCTION_FULL.z, y: 1.3, r: 1.6,
    prompt: 'THE JUNCTION SWITCHBOARD — SILENT, FOR NOW' },
  { id: 'junctiontap', kind: 'lectern', x: JUNCTION_TAP.x, z: JUNCTION_TAP.z, y: 1.3, r: 1.4,
    prompt: 'JUNCTION RELIEF — GRADE-1 TAP — THE FULL RELIEF IS TAKEN IN THE MAIL ROOM' },
  // 5-D forensic row now lives on Inspection Row; grade-0/1 intro kept at Window Row
  { id: 'discwindow', kind: 'lectern', x: DISC_FULL.x, z: DISC_FULL.z, y: 1.3, r: 1.6,
    prompt: 'THE DISCREPANCY WINDOW — CLOSED' },
  { id: 'discwindowtap', kind: 'lectern', x: DISC_TEASER.x, z: DISC_TEASER.z, y: 1.3, r: 1.4,
    prompt: 'DISCREPANCY INTAKE — GRADE-1 — FULL INSPECTION IS HELD ON INSPECTION ROW' },
  { id: 'terminals', kind: 'lectern', x: -15, z: -15.3, y: 1.3, r: 1.6,
    prompt: 'TERMINAL SUPPORT BAY — NO TICKETS OPEN' },
  { id: 'armature', kind: 'lectern', x: -11, z: -15.3, y: 1.3, r: 1.5,
    prompt: 'THE ARMATURE STATION — ALIGNED, PRESUMABLY' },
];

// ---------------------------------------------------------------------------
// Player
// ---------------------------------------------------------------------------

export const SPAWN = { x: 24, z: 1, facing: Math.PI / 2 }; // facing west
// M-C: envelope grows NORTH to z=-25 (back-of-house strip) and WEST to x=-38
// (service spine). The v1 rectangle x[-30,30] z[-17,17] is preserved verbatim.
export const WORLD_BOUNDS = { x0: -38, z0: -25, x1: 30, z1: 17 };
